import { useEffect } from 'react'

/**
 * Reactivating a suspended company restores the normal UI with a full page load
 * (`companyAccessReactivation.restore`), and that load throws away whatever a member is typing.
 * Surfaces that stay usable while suspended and hold unsaved input (the profile edit dialog, the
 * feedback drawer) hold the reload while they are open; it runs once the last one closes.
 * (SC-409)
 *
 * Kept free of imports beyond React so those components can use it without pulling in the
 * access store and its services.
 */
let holds = 0
const releaseListeners = new Set<() => void>()

export function isCompanyAccessReloadHeld(): boolean {
  return holds > 0
}

/** Called each time the last hold is released. Returns the unsubscribe function. */
export function onCompanyAccessReloadReleased(listener: () => void): () => void {
  releaseListeners.add(listener)
  return () => {
    releaseListeners.delete(listener)
  }
}

/** Holds the reactivation reload while `active` (typically the surface's `open`) is true. */
export function useHoldCompanyAccessReload(active: boolean): void {
  useEffect(() => {
    if (!active) return
    holds += 1
    return () => {
      holds -= 1
      if (holds === 0) Array.from(releaseListeners).forEach((listener) => listener())
    }
  }, [active])
}
